import React from 'react';

const UniversalLoanLoader = ({ 
  type = 'personal', 
  title = 'Processing Your Loan Application',
  subtitle = 'Finding the best loan options for you...',
  theme = 'blue' 
}) => {
  
  // Theme configurations
  const themes = {
    blue: {
      gradient: 'from-blue-50 to-indigo-100',
      primary: '#3B82F6',
      secondary: '#1E40AF',
      accent: '#FBBF24'
    },
    green: {
      gradient: 'from-green-50 to-emerald-100',
      primary: '#10B981',
      secondary: '#047857',
      accent: '#F59E0B'
    },
    purple: {
      gradient: 'from-purple-50 to-violet-100',
      primary: '#8B5CF6',
      secondary: '#5B21B6',
      accent: '#EF4444'
    },
    orange: {
      gradient: 'from-orange-50 to-amber-100',
      primary: '#F97316',
      secondary: '#EA580C',
      accent: '#06B6D4'
    },
    gold: {
      gradient: 'from-yellow-50 to-amber-100',
      primary: '#F59E0B',
      secondary: '#D97706',
      accent: '#10B981'
    },
    red: {
      gradient: 'from-red-50 to-rose-100',
      primary: '#EF4444',
      secondary: '#DC2626',
      accent: '#3B82F6'
    }
  };

  const currentTheme = themes[theme] || themes.blue;

  // Icon configurations for different loan types
  const getLoanIcon = () => {
    switch(type) {
      case 'car':
        return (
          <svg width="80" height="50" viewBox="0 0 80 50" className="loan-icon">
            <rect x="10" y="25" width="50" height="15" rx="3" fill={currentTheme.primary} className="main-shape"/>
            <path d="M20 25 L25 15 L45 15 L50 25 Z" fill={currentTheme.secondary} className="secondary-shape"/>
            <rect x="22" y="17" width="8" height="6" rx="1" fill="#E0F2FE" className="detail"/>
            <rect x="35" y="17" width="8" height="6" rx="1" fill="#E0F2FE" className="detail"/>
            <circle cx="22" cy="37" r="6" fill="#374151" className="wheel wheel-1"/>
            <circle cx="48" cy="37" r="6" fill="#374151" className="wheel wheel-2"/>
            <circle cx="22" cy="37" r="3" fill="#6B7280"/>
            <circle cx="48" cy="37" r="3" fill="#6B7280"/>
            <path d="M55 20 Q60 15 65 20 Q60 25 55 20 Z" fill={currentTheme.accent} className="money-symbol pulse-highlight"/>
          </svg>
        );
      
      case 'gold':
        return (
          <svg width="80" height="50" viewBox="0 0 80 50" className="loan-icon">
            <rect x="25" y="15" width="30" height="20" rx="3" fill={currentTheme.primary} className="main-shape"/>
            <rect x="27" y="17" width="26" height="16" rx="2" fill="#FEF3C7" className="detail"/>
            <circle cx="40" cy="25" r="6" fill={currentTheme.secondary} className="secondary-shape"/>
            <circle cx="40" cy="25" r="3" fill="#FCD34D" className="gold-center pulse-highlight"/>
            <rect x="32" y="22" width="2" height="6" fill={currentTheme.accent} className="detail"/>
            <rect x="46" y="22" width="2" height="6" fill={currentTheme.accent} className="detail"/>
            <path d="M20 30 Q25 28 30 30" stroke={currentTheme.accent} strokeWidth="2" fill="none" className="sparkle"/>
            <path d="M50 30 Q55 28 60 30" stroke={currentTheme.accent} strokeWidth="2" fill="none" className="sparkle"/>
          </svg>
        );
      
      case 'home':
        return (
          <svg width="80" height="50" viewBox="0 0 80 50" className="loan-icon">
            <path d="M20 25 L40 10 L60 25 L60 40 L20 40 Z" fill={currentTheme.primary} className="main-shape"/>
            <rect x="30" y="30" width="8" height="10" fill={currentTheme.secondary} className="secondary-shape"/>
            <rect x="32" y="32" width="4" height="4" fill={currentTheme.accent} className="detail"/>
            <rect x="45" y="20" width="6" height="6" fill="white" className="detail"/>
            <circle cx="15" cy="20" r="3" fill={currentTheme.accent} className="money-circle pulse-highlight"/>
            <text x="15" y="24" textAnchor="middle" fontSize="8" fill="white" className="money-text">₹</text>
            <circle cx="65" cy="35" r="3" fill={currentTheme.accent} className="money-circle pulse-highlight"/>
            <text x="65" y="39" textAnchor="middle" fontSize="8" fill="white" className="money-text">₹</text>
          </svg>
        );
      
      case 'personal':
        return (
          <svg width="80" height="50" viewBox="0 0 80 50" className="loan-icon">
            <circle cx="40" cy="20" r="8" fill={currentTheme.primary} className="main-shape"/>
            <path d="M32 28 Q40 35 48 28 L45 40 L35 40 Z" fill={currentTheme.secondary} className="secondary-shape"/>
            <circle cx="40" cy="20" r="4" fill="white" className="detail"/>
            <rect x="25" y="30" width="30" height="4" rx="2" fill={currentTheme.accent} className="progress-bar"/>
            <rect x="25" y="30" width="15" height="4" rx="2" fill={currentTheme.primary} className="progress-fill pulse-highlight"/>
            <text x="55" y="33" fontSize="6" fill={currentTheme.secondary} className="percentage">50%</text>
          </svg>
        );
      
      case 'business':
        return (
          <svg width="80" height="50" viewBox="0 0 80 50" className="loan-icon">
            <rect x="20" y="20" width="40" height="20" rx="2" fill={currentTheme.primary} className="main-shape"/>
            <rect x="22" y="22" width="36" height="3" fill="white" className="detail"/>
            <rect x="22" y="27" width="36" height="3" fill="white" className="detail"/>
            <rect x="22" y="32" width="20" height="3" fill="white" className="detail"/>
            <path d="M30 10 L35 5 L45 5 L50 10 L50 20 L30 20 Z" fill={currentTheme.secondary} className="secondary-shape"/>
            <rect x="35" y="8" width="3" height="8" fill={currentTheme.accent} className="building-detail"/>
            <rect x="42" y="8" width="3" height="8" fill={currentTheme.accent} className="building-detail"/>
            <circle cx="65" cy="15" r="4" fill={currentTheme.accent} className="growth-circle pulse-highlight"/>
            <path d="M63 17 L65 15 L67 17" stroke="white" strokeWidth="1" fill="none" className="arrow-up"/>
          </svg>
        );
      
      case 'education':
        return (
          <svg width="80" height="50" viewBox="0 0 80 50" className="loan-icon">
            <path d="M15 25 L40 15 L65 25 L40 35 Z" fill={currentTheme.primary} className="main-shape"/>
            <rect x="35" y="20" width="10" height="10" fill={currentTheme.secondary} className="secondary-shape"/>
            <circle cx="40" cy="25" r="3" fill="white" className="detail"/>
            <rect x="25" y="35" width="30" height="3" fill={currentTheme.accent} className="base"/>
            <path d="M20 10 Q25 8 30 10" stroke={currentTheme.accent} strokeWidth="2" fill="none" className="knowledge-wave"/>
            <path d="M50 10 Q55 8 60 10" stroke={currentTheme.accent} strokeWidth="2" fill="none" className="knowledge-wave"/>
            <circle cx="70" cy="20" r="2" fill={currentTheme.accent} className="star pulse-highlight"/>
            <circle cx="10" cy="30" r="2" fill={currentTheme.accent} className="star pulse-highlight"/>
          </svg>
        );
      
      case 'twowheeler':
        return (
          <svg width="80" height="50" viewBox="0 0 80 50" className="loan-icon">
            <path d="M25 25 L30 20 L50 20 L55 25 L50 30 L30 30 Z" fill={currentTheme.primary} className="main-shape"/>
            <rect x="32" y="22" width="16" height="6" rx="1" fill="white" className="detail"/>
            <circle cx="22" cy="35" r="7" fill="#374151" className="wheel wheel-1"/>
            <circle cx="58" cy="35" r="7" fill="#374151" className="wheel wheel-2"/>
            <circle cx="22" cy="35" r="4" fill="#6B7280"/>
            <circle cx="58" cy="35" r="4" fill="#6B7280"/>
            <path d="M35 15 L40 10 L45 15" stroke={currentTheme.secondary} strokeWidth="2" fill="none" className="handle"/>
            <circle cx="40" cy="12" r="2" fill={currentTheme.accent} className="headlight pulse-highlight"/>
            <path d="M60 20 Q65 18 70 20" stroke={currentTheme.accent} strokeWidth="2" fill="none" className="speed-line"/>
          </svg>
        );
      
      default:
        return (
          <svg width="80" height="50" viewBox="0 0 80 50" className="loan-icon">
            <rect x="20" y="15" width="40" height="20" rx="5" fill={currentTheme.primary} className="main-shape"/>
            <circle cx="40" cy="25" r="8" fill="white" className="detail"/>
            <text x="40" y="29" textAnchor="middle" fontSize="12" fill={currentTheme.secondary} className="rupee-symbol">₹</text>
            <path d="M30 35 Q40 40 50 35" stroke={currentTheme.accent} strokeWidth="2" fill="none" className="money-flow pulse-highlight"/>
          </svg>
        );
    }
  };

  // Get floating icons based on loan type
  const getFloatingIcons = () => {
    const iconSets = {
      car: ['🚗', '💰', '📋', '✅'],
      gold: ['💰', '🏆', '📊', '💎'],
      home: ['🏠', '🔑', '💰', '📋'],
      personal: ['👤', '💰', '📱', '✨'],
      business: ['🏢', '📈', '💼', '🎯'],
      education: ['🎓', '📚', '💡', '🎯'],
      twowheeler: ['🏍️', '💨', '💰', '⚡'],
      default: ['💰', '📋', '✅', '📊']
    };
    
    return iconSets[type] || iconSets.default;
  };

  const floatingIcons = getFloatingIcons();

  return (
    <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br ${currentTheme.gradient}`}>
      <div className="relative">
        {/* Main container */}
        <div className="relative bg-white rounded-2xl shadow-2xl p-8 w-80 h-64 flex flex-col items-center justify-center">
          
          {/* Animated loan icon */}
          <div className="relative mb-6">
            <div className="icon-container">
              {getLoanIcon()}
            </div>
            
            {/* Progress bar for most types */}
            {!['car', 'twowheeler'].includes(type) && (
              <div className="progress-bar-container mt-4">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ backgroundColor: currentTheme.primary }}></div>
                </div>
                <div className="progress-text mt-1 text-xs text-gray-600">Processing Application...</div>
              </div>
            )}
            
            {/* Road animation for car and twowheeler types */}
            {['car', 'twowheeler'].includes(type) && (
              <div className="road-container mt-4">
                <div className="road-line" style={{ backgroundColor: currentTheme.accent }}></div>
                <div className="road-line road-line-2" style={{ backgroundColor: currentTheme.accent }}></div>
                <div className="road-line road-line-3" style={{ backgroundColor: currentTheme.accent }}></div>
              </div>
            )}
          </div>
          
          {/* Loading text */}
          <div className="text-center">
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              <span className="typing-text">{title}</span>
              <span className="cursor">|</span>
            </h3>
            <p className="text-gray-600 text-sm">{subtitle}</p>
          </div>
          
          {/* Progress dots */}
          <div className="flex space-x-2 mt-4">
            <div className="dot dot-1" style={{ backgroundColor: currentTheme.primary }}></div>
            <div className="dot dot-2" style={{ backgroundColor: currentTheme.primary }}></div>
            <div className="dot dot-3" style={{ backgroundColor: currentTheme.primary }}></div>
            <div className="dot dot-4" style={{ backgroundColor: currentTheme.primary }}></div>
          </div>
        </div>
        
        {/* Floating loan icons */}
        {floatingIcons.map((icon, index) => (
          <div key={index} className={`floating-icon icon-${index + 1}`}>
            {icon}
          </div>
        ))}
      </div>
      
      <style jsx>{`
        .icon-container {
          animation: bounce 2s ease-in-out infinite;
        }
        
        .main-shape {
          animation: pulse 1.5s ease-in-out infinite;
        }
        
        .secondary-shape {
          animation: pulse 2s ease-in-out infinite;
        }
        
        .wheel {
          animation: spin 1s linear infinite;
          transform-origin: center;
        }
        
        .pulse-highlight {
          animation: pulseGlow 1.5s ease-in-out infinite;
        }
        
        .sparkle {
          animation: sparkleMove 2s ease-in-out infinite;
        }
        
        .knowledge-wave {
          animation: wave 2s ease-in-out infinite;
        }
        
        .speed-line {
          animation: speedMove 1s linear infinite;
        }
        
        .money-flow {
          animation: flowMove 2s ease-in-out infinite;
        }
        
        .road-container {
          height: 4px;
          background: #374151;
          width: 120px;
          position: relative;
          border-radius: 2px;
          overflow: hidden;
        }
        
        .road-line {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          height: 2px;
          width: 20px;
          border-radius: 1px;
          animation: roadMove 1s linear infinite;
        }
        
        .road-line-2 {
          animation-delay: -0.3s;
        }
        
        .road-line-3 {
          animation-delay: -0.6s;
        }
        
        .progress-bar-container {
          width: 120px;
        }
        
        .progress-bar {
          height: 4px;
          background: #E5E7EB;
          border-radius: 2px;
          overflow: hidden;
        }
        
        .progress-fill {
          height: 100%;
          width: 0%;
          border-radius: 2px;
          animation: progressFill 2s ease-in-out infinite;
        }
        
        .progress-text {
          text-align: center;
          animation: fadeInOut 2s ease-in-out infinite;
        }
        
        .typing-text {
          animation: typewriter 3s steps(30) infinite;
        }
        
        .cursor {
          animation: blink 1s step-end infinite;
        }
        
        .dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          animation: dotPulse 1.4s ease-in-out infinite both;
        }
        
        .dot-2 { animation-delay: -0.32s; }
        .dot-3 { animation-delay: -0.16s; }
        .dot-4 { animation-delay: 0s; }
        
        .floating-icon {
          position: absolute;
          font-size: 24px;
          animation: float 3s ease-in-out infinite;
        }
        
        .icon-1 {
          top: -20px;
          left: -20px;
          animation-delay: 0s;
        }
        
        .icon-2 {
          top: -20px;
          right: -20px;
          animation-delay: -1s;
        }
        
        .icon-3 {
          bottom: -20px;
          left: -20px;
          animation-delay: -2s;
        }
        
        .icon-4 {
          bottom: -20px;
          right: -20px;
          animation-delay: -1.5s;
        }
        
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0) scale(1);
          }
          40% {
            transform: translateY(-10px) scale(1.05);
          }
          60% {
            transform: translateY(-5px) scale(1.02);
          }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        
        @keyframes pulseGlow {
          0%, 100% { 
            opacity: 1; 
            transform: scale(1);
            filter: brightness(1);
          }
          50% { 
            opacity: 0.7; 
            transform: scale(1.2);
            filter: brightness(1.5);
          }
        }
        
        @keyframes sparkleMove {
          0%, 100% { 
            opacity: 0.5; 
            transform: translateY(0px);
          }
          50% { 
            opacity: 1; 
            transform: translateY(-5px);
          }
        }
        
        @keyframes wave {
          0%, 100% { 
            opacity: 0.6; 
            transform: translateY(0px);
          }
          50% { 
            opacity: 1; 
            transform: translateY(-3px);
          }
        }
        
        @keyframes speedMove {
          0% { opacity: 0; transform: translateX(-20px); }
          50% { opacity: 1; }
          100% { opacity: 0; transform: translateX(20px); }
        }
        
        @keyframes flowMove {
          0%, 100% { 
            opacity: 0.5; 
            stroke-dasharray: 0 100;
          }
          50% { 
            opacity: 1; 
            stroke-dasharray: 50 50;
          }
        }
        
        @keyframes roadMove {
          0% { left: -30px; }
          100% { left: 130px; }
        }
        
        @keyframes progressFill {
          0% { width: 0%; }
          70% { width: 100%; }
          100% { width: 100%; }
        }
        
        @keyframes fadeInOut {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
        
        @keyframes typewriter {
          0% { width: 0; }
          50% { width: 100%; }
          100% { width: 0; }
        }
        
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        
        @keyframes dotPulse {
          0%, 80%, 100% {
            transform: scale(0);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          33% {
            transform: translateY(-10px) rotate(5deg);
          }
          66% {
            transform: translateY(-5px) rotate(-5deg);
          }
        }
      `}</style>
    </div>
  );
};

export default UniversalLoanLoader;